import React, { useState, useCallback, useEffect, useContext, useRef } from 'react';
import dayjs from 'dayjs';
import { Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import FilterPanel from '../components/FilterPanel';
import ResultsTable from '../components/ResultsTable';
import SidePanel from '../components/SidePanel';
import api from '../api';
import { loadPageCache, savePageCache } from '../utils/pageCache';

// Runs at module load time (before React renders) so sessionStorage is ready
// before FilterPanel's useEffect reads it. Triggered by EvalProgress "Find Qualified Calls".
let _presetWasLoaded = false;
(function () {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('from_preset') !== '1') return;
    const raw = localStorage.getItem('qa_search_preset');
    if (!raw) return;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts < 30000) {
      sessionStorage.setItem('fp_search', JSON.stringify(data));
      sessionStorage.removeItem('page_cache_search'); // clear stale results
      _presetWasLoaded = true;
    }
    localStorage.removeItem('qa_search_preset');
    window.history.replaceState({}, '', window.location.pathname);
  } catch {}
}());

export default function SearchPage() {
  const currentUser = useContext(UserContext);

  const _cache = loadPageCache('search');
  const [results, setResults]             = useState(_cache?.results ?? []);
  const [total, setTotal]                 = useState(_cache?.total ?? 0);
  const [page, setPage]                   = useState(_cache?.page ?? 1);
  const [pageSize, setPageSize]           = useState(_cache?.pageSize ?? 100);
  const [loading, setLoading]             = useState(false);
  const [activeFilters, setActiveFilters] = useState(_cache?.activeFilters ?? {});
  const [selectedId, setSelectedId]       = useState(null);
  const [panelOpen, setPanelOpen]         = useState(false);
  const [caseCounts, setCaseCounts]       = useState({});  // { [contactId]: count }
  const [sortState, setSortState] = useState({ field: 'Date', order: 'descend' });
  const sortRef = useRef({ field: 'Date', order: 'descend' });
  const filterPanelRef = useRef();

  const refreshCaseCounts = useCallback(async () => {
    try {
      const res = await api.get('/api/cases/contact-counts');
      setCaseCounts(res.data);
    } catch {
      // non-critical
    }
  }, []);

  // When results are restored from cache, still need fresh case counts for the icons
  useEffect(() => {
    if (_cache?.results?.length) refreshCaseCounts();
  }, []); // run once on mount

  // Auto-trigger search when opened from EvalProgress "Find Qualified Calls"
  useEffect(() => {
    if (!_presetWasLoaded) return;
    const t = setTimeout(() => filterPanelRef.current?.triggerSearch(), 150);
    return () => clearTimeout(t);
  }, []); // run once on mount

  const fetchResults = useCallback(async (filters, newPage = 1, newPageSize = pageSize) => {
    setLoading(true);
    try {
      const sort = sortRef.current;
      const params = { ...filters, page: newPage, page_size: newPageSize };
      if (sort?.field && sort?.order) {
        params.sort_by  = sort.field;
        params.sort_dir = sort.order === 'ascend' ? 'asc' : 'desc';
      }
      const res = await api.get('/api/calls', { params });
      setResults(res.data.data);
      setTotal(res.data.total);
      setPage(res.data.page);
      savePageCache('search', {
        results: res.data.data, total: res.data.total,
        page: res.data.page, pageSize: newPageSize, activeFilters: filters,
      });
      refreshCaseCounts();
    } catch {
      message.error('Failed to load results. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pageSize, refreshCaseCounts]);

  const handleSearch = (filters) => {
    setActiveFilters(filters);
    fetchResults(filters, 1, pageSize);
  };


  const handlePageChange = (newPage, newPageSize) => {
    setPageSize(newPageSize);
    fetchResults(activeFilters, newPage, newPageSize);
  };

  const handleSortChange = (field, order) => {
    // When sort is cleared (order=null), fall back to default date desc
    const next = (field && order)
      ? { field, order }
      : { field: 'Date', order: 'descend' };
    sortRef.current = next;
    setSortState(next);
    fetchResults(activeFilters, 1, pageSize);
  };

  const handleRowClick = (record) => {
    setSelectedId(record.ContactID);
    setPanelOpen(true);
  };

  const handleAssign = (record) => {
    setSelectedId(record.ContactID);
    setPanelOpen(true);
  };

  const handleEvalCreated = useCallback(() => {
    setSelectedId((id) => {
      if (id) setCaseCounts((prev) => ({ ...prev, [String(id)]: (prev[String(id)] ?? 0) + 1 }));
      return id;
    });
  }, []);

  const handleFlagToggle = (contactId, nowFlagged) => {
    setResults((prev) =>
      prev.map((r) =>
        r.ContactID === contactId
          ? {
              ...r,
              UserHasFlagged: nowFlagged ? 1 : 0,
              FlagCount: nowFlagged ? r.FlagCount + 1 : Math.max(0, r.FlagCount - 1),
            }
          : r
      )
    );
  };

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        Call Search
      </Typography.Title>

      <FilterPanel
        ref={filterPanelRef}
        sessionKey="search"
        onSearch={handleSearch}
        loading={loading}
        requireCampaign
        showCalledFilters
        defaultDateRange={[dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')]}
      />

      <ResultsTable
        data={results}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onRowClick={handleRowClick}
        onFlagToggle={handleFlagToggle}
        onAssign={currentUser != null && ((currentUser.CanEditEvals ?? 0) >= 2 || (currentUser.CanEditErrors ?? 0) >= 2) ? handleAssign : undefined}
        caseCounts={caseCounts}
        onPageChange={handlePageChange}
        sortState={sortState}
        onSortChange={handleSortChange}
      />

      <SidePanel
        contactId={selectedId}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onEvalCreated={handleEvalCreated}
      />
    </>
  );
}
